import React, { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import { range } from "lodash";

import { datadogRum } from "@datadog/browser-rum";
import { getMilliseconds } from "date-fns";

import "./WorkflowMenu.css";

import DraggableTilePlaceholder from "components/DraggableTile/components/DraggableTilePlaceholder";

import { HubContext } from "scenes/Hubly/components/Workspace/Provider";

import { SCREEN_TYPE } from "../../Container";
import { useScreenManager } from "..";
import { useEditorData, useEditorState, useFetchWithStats } from "../hooks";
import {
  ProcessList,
  ProcessSettingModal,
  SearchInput,
  TopButtons,
  WorkflowList,
  WorkflowMenuButton,
  WorkflowMenuLayout,
  WorkflowTilePlaceholder,
} from "./components";
import { getAllWorkflows, getWorkflowsWithNoProcessEmptyText } from "./helper";

export function WorkflowMenu({ selectedWorkflows, onChange, selectedFilters }) {
  const { fetchProcesses: fp } = useContext(HubContext);
  const fetchProcesses = useFetchWithStats(fp);

  const [popupOpen, setPopupOpen] = useState(false);
  const [spentTimeOnWorkfilteOpen, setSpentTimeOnWorkfilteOpen] = useState(0);
  const [settingProps, setSettingProps] = useState(null);
  const [filteredKeyword, setFilteredKeyword] = useState("");

  const [processes, dispatchProcesses] = useEditorState();
  const [workflows, dispatchWorkflows] = useEditorState();

  const { loading, reloadData, numOfFetches } = useEditorData(fetchProcesses, {
    dispatch: useCallback(
      ({ type, value: [p, w] }) => {
        dispatchProcesses({ type: type, value: p });
        dispatchWorkflows({ type: type, value: w });
      },
      [dispatchProcesses, dispatchWorkflows]
    ),
  });
  const screen = useScreenManager();

  const hubWorkflowsTotal =
    workflows.length +
    processes.reduce((total, process) => {
      return total + process.workflows.length;
    }, 0);

  const onDeleteProcess = (processId) => {
    onChange({
      workflows: selectedWorkflows.map((w) => {
        return w.process?.id === processId ? { ...w, process: null } : w;
      }),
    });
    reloadData();
  };

  const onUpdateProcess = (process) => {
    onChange({
      workflows: selectedWorkflows.map((w) => {
        return {
          ...w,
          process: process.workflows.includes(w.id) ? process : undefined,
        };
      }),
    });
    reloadData();
  };

  const onCreateProcess = (process) => {
    onChange({
      workflows: selectedWorkflows.map((w) => {
        return process.workflows.includes(w.id) ? { ...w, process } : w;
      }),
    });
    reloadData();
  };

  const handleWorkflowSelection = (updatedWorkflows) => {
    onChange({
      workflows: updatedWorkflows,
      showAll: false,
    });
  };

  return (
    <React.Fragment>
      <WorkflowMenuLayout
        opened={popupOpen}
        onOpen={() => {
          setSpentTimeOnWorkfilteOpen(new Date().getTime());
          setPopupOpen(true);
          reloadData();
          if (screen.front() !== SCREEN_TYPE.WORKFLOW)
            screen.promote(SCREEN_TYPE.WORKFLOW);
        }}
        onMount={() => {
          datadogRum.addAction(
            "hubly_timespent_workflowfilter_display_workflowprocesmenu",
            { duration: getMilliseconds(spentTimeOnWorkfilteOpen) }
          );
          setSpentTimeOnWorkfilteOpen(0);
        }}
        onClose={() => {
          setPopupOpen(false);
        }}
        trigger={
          <WorkflowMenuButton totalWorkflow={selectedWorkflows.length} />
        }
      >
        <TopButtons
          disabled={hubWorkflowsTotal === 0}
          showAll={selectedWorkflows.length < hubWorkflowsTotal}
          onHideAllClick={() => {
            onChange({
              workflows: [],
              filters: selectedFilters,
              showAll: false,
            });
          }}
          onShowAllClick={() => {
            onChange({
              workflows: getAllWorkflows(processes, workflows),
              filters: { ...selectedFilters, filterEmptyWorkflows: false },
              showAll: true,
            });
          }}
          onShowActiveClick={() => {
            onChange({
              workflows: getAllWorkflows(processes, workflows),
              filters: { ...selectedFilters, filterEmptyWorkflows: true },
              showAll: true,
            });
          }}
          onCreateProcessClick={() => {
            setSettingProps({
              showEdit: true,
              showDelete: false,
              showGatedTemplate: true,
              showShareProcess: false,
              process: null,
            });
          }}
        />
        <SearchInput value={filteredKeyword} onChange={setFilteredKeyword} />
        <ProcessList
          processes={processes}
          loading={loading}
          placeholder={
            numOfFetches === 0 &&
            loading &&
            range(6).map((i) => {
              return <DraggableTilePlaceholder key={`processTile${i}`} />;
            })
          }
          onReordered={(orderedProcesses) => {
            dispatchProcesses({ type: "set", value: orderedProcesses });
          }}
          onEditProcessClick={(process) => {
            setSettingProps({
              showEdit: true,
              showDelete: false,
              showShareProcess: false,
              showGatedTemplate: false,
              process: process,
            });
          }}
          onDeleteProcessClick={(process) => {
            setSettingProps({
              showEdit: false,
              showDelete: true,
              showShareProcess: false,
              showGatedTemplate: false,
              process: process,
            });
          }}
          onShareProcessClick={(process) => {
            setSettingProps({
              showEdit: false,
              showDelete: false,
              showShareProcess: true,
              showGatedTemplate: false,
              process: process,
            });
          }}
          onCreateTemplateClick={(process) => {
            setSettingProps({
              showEdit: false,
              showDelete: false,
              showShareProcess: false,
              showGatedTemplate: true,
              process: process,
            });
          }}
          selectedWorkflows={selectedWorkflows}
          filteredKeyword={filteredKeyword}
          onChange={handleWorkflowSelection}
        >
          {(process) => {
            return (
              <WorkflowList
                id="process-workflows"
                selectedWorkflows={selectedWorkflows}
                filteredKeyword={filteredKeyword}
                workflows={process.workflows}
                onReordered={(orderedWorkflows) => {
                  dispatchProcesses({
                    type: "edit",
                    value: { ...process, workflows: orderedWorkflows },
                  });
                }}
                onChange={handleWorkflowSelection}
                onClientsAdded={reloadData}
              />
            );
          }}
        </ProcessList>
        <WorkflowList
          id="workflow-with-no-process"
          selectedWorkflows={selectedWorkflows}
          filteredKeyword={filteredKeyword}
          workflows={workflows}
          loading={loading}
          showHeader
          placeholder={
            numOfFetches === 0 &&
            loading &&
            range(3).map((i) => {
              return <WorkflowTilePlaceholder key={`workflowTile${i}`} />;
            })
          }
          emptyText={getWorkflowsWithNoProcessEmptyText(
            hubWorkflowsTotal,
            filteredKeyword,
            workflows.length
          )}
          onReordered={(orderedWorkflows) => {
            dispatchWorkflows({
              type: "set",
              value: orderedWorkflows,
            });
          }}
          onChange={handleWorkflowSelection}
          onClientsAdded={reloadData}
        />
      </WorkflowMenuLayout>
      {settingProps && (
        <ProcessSettingModal
          {...settingProps}
          onCreateProcess={onCreateProcess}
          onUpdateProcess={onUpdateProcess}
          onDeleteProcess={onDeleteProcess}
          onCreateTemplate={reloadData}
          onShareProcess={reloadData}
          onClose={setSettingProps}
        />
      )}
    </React.Fragment>
  );
}

WorkflowMenu.propTypes = {
  selectedWorkflows: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  selectedFilters: PropTypes.object.isRequired,
};

export default WorkflowMenu;
